import { Person } from "@weps/shared-zok-models";

/**
 * Input data for duplicate person matching.
 * Contains the minimal fields needed to check for a duplicate.
 */
export interface PersonMatchInput {
  dateOfBirth: string;
}

/**
 * Finds a person in the household with matching date of birth.
 *
 * @param input - The person data to check for duplicates (only dateOfBirth is used)
 * @param householdPeople - Array of existing household members to check against
 * @param excludeHouseholdMemberId - Optional ID to exclude from matching (useful in edit mode to exclude self)
 * @returns The matching Person if found, null otherwise
 *
 * @example
 * ```typescript
 * const input = { dateOfBirth: '1980-01-15' };
 * const match = findMatchingPerson(input, householdMembers);
 * if (match) {
 *   console.log('Duplicate found:', match.householdMemberId);
 * }
 * ```
 */
export function findMatchingPerson(
  input: PersonMatchInput,
  householdPeople: Person[],
  excludeHouseholdMemberId?: string,
): Person | null {
  const inputDob = input.dateOfBirth;

  // Return null if DOB is empty
  if (!inputDob) return null;

  for (const person of householdPeople) {
    // Skip the person being edited in edit mode
    if (excludeHouseholdMemberId && person.householdMemberId === excludeHouseholdMemberId) {
      continue;
    }

    if (inputDob === person.birthDate) {
      return person;
    }
  }

  return null;
}
